import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Role } from '@/features/auth/roles';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';

export interface StaffRef {
  id: string;
  fullName: string;
}

export interface ThreadSummary {
  id: string;
  title: string;
  patient: { id: string; fullName: string } | null;
  deskRole: Role | null;
  participants: StaffRef[];
  lastMessage: string;
  lastMessageAt: string;
  unread: boolean;
}

export interface Thread {
  id: string;
  title: string;
  patient: { id: string; fullName: string } | null;
  deskRole: Role | null;
  branch: { id: string; name: string };
  participants: StaffRef[];
  messages: { id: string; body: string; author: StaffRef; createdAt: string }[];
}

export interface Directory {
  staff: { id: string; fullName: string; roles: string[] }[];
  desks: { key: Role; name: string }[];
}

const LIVE_MS = 10_000;

function useBranchId() {
  return useBranch().selected?.id ?? null;
}

export function useThreads() {
  const branch = useBranchId();
  return useQuery({
    queryKey: ['team-messages', branch],
    queryFn: async ({ signal }) =>
      (await api.get<ThreadSummary[]>('/team-messages/threads', { signal }))
        .data,
    enabled: !!branch,
    refetchInterval: LIVE_MS,
  });
}

export function useThread(id: string | null) {
  const branch = useBranchId();
  return useQuery({
    queryKey: ['team-messages', branch, 'thread', id],
    queryFn: async ({ signal }) =>
      (await api.get<Thread>(`/team-messages/threads/${id}`, { signal })).data,
    enabled: !!branch && !!id,
    refetchInterval: LIVE_MS,
  });
}

export function useDirectory(enabled: boolean) {
  const branch = useBranchId();
  return useQuery({
    queryKey: ['team-messages', branch, 'directory'],
    queryFn: async ({ signal }) =>
      (await api.get<Directory>('/team-messages/directory', { signal })).data,
    enabled: enabled && !!branch,
  });
}

export function useMessagingActions() {
  const qc = useQueryClient();
  const refresh = () =>
    void qc.invalidateQueries({ queryKey: ['team-messages'] });
  return {
    create: useMutation({
      mutationFn: async (v: {
        patientId?: string;
        deskRole?: Role;
        participantIds: string[];
        body: string;
      }) => (await api.post<Thread>('/team-messages/threads', v)).data,
      onSuccess: refresh,
    }),
    send: useMutation({
      mutationFn: async (v: { threadId: string; body: string }) =>
        (
          await api.post<Thread>(
            `/team-messages/threads/${v.threadId}/messages`,
            {
              body: v.body,
            },
          )
        ).data,
      onSuccess: refresh,
    }),
  };
}
